import { useEffect, useMemo, useState } from "react";
import { api, formatNum, downloadExport } from "@/lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionsContext";
import { groupByCategory } from "@/lib/groupBy";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BookOpen, Search, Clock, Lightbulb, Pencil, ChefHat, Printer, FileText, FileSpreadsheet } from "lucide-react";
import { GuideEditor } from "@/components/guide/GuideEditor";
import { RecipePhoto } from "@/components/guide/RecipePhoto";

export default function Guide() {
  const { user } = useAuth();
  const { mine } = usePermissions();
  const [recipes, setRecipes] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = async () => { const r = await api.get("/recipes"); setRecipes(r.data); return r.data; };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return ql ? recipes.filter((r) => r.display_name.toLowerCase().includes(ql) || r.category.toLowerCase().includes(ql)) : recipes;
  }, [recipes, q]);
  const sel = recipes.find((r) => r.id === selected) || null;
  const canEdit = (r) => user?.role === "admin" || (!r.locked && (mine || []).includes("recipes"));
  const hasGuide = (r) => r.guide?.steps?.length > 0;
  const exportFile = async (ext) => {
    try { await downloadExport(`/export/guide.${ext}`, {}, `fithouse_tarifler.${ext}`); toast.success(`${ext.toUpperCase()} dosyası indirildi`); }
    catch (e) { toast.error("Dışa aktarma başarısız"); }
  };

  return (
    <div className="space-y-6" data-testid="guide-page">
      <div className="flex flex-wrap items-end justify-between gap-3 print:hidden">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Tarifler</h1>
          <p className="mt-1 text-sm text-muted-foreground">Ürünlerin hazırlanışı — malzemeler reçeteden otomatik gelir</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" data-testid="export-guide-pdf" onClick={() => exportFile("pdf")}><FileText className="mr-1.5 h-4 w-4 text-destructive" /> PDF</Button>
          <Button variant="secondary" data-testid="export-guide-xlsx" onClick={() => exportFile("xlsx")}><FileSpreadsheet className="mr-1.5 h-4 w-4 text-primary" /> Excel</Button>
          <div className="relative w-64">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Ürün ara…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 pl-8" data-testid="guide-search" />
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <div className="rounded-xl border border-border bg-card lg:col-span-2 print:hidden" data-testid="guide-list">
          {groupByCategory(filtered, "product_name").map((g) => [
            <div key={`h-${g.category}`} className="bg-secondary/30 px-4 py-2 font-heading text-xs font-extrabold uppercase tracking-[0.2em] text-primary">{g.category}</div>,
            ...g.items.map((r) => (
              <button key={r.id} type="button" data-testid={`guide-item-${r.id}`} onClick={() => { setSelected(r.id); setEditing(false); }}
                className={`flex w-full items-center justify-between gap-2 border-b border-border/60 px-4 py-2.5 text-left transition-colors last:border-0 ${selected === r.id ? "bg-primary/10" : "hover:bg-secondary/20"}`}>
                <span className="min-w-0">
                  <span className="block truncate font-heading font-bold">{r.display_name}</span>
                  <span className="block text-xs text-muted-foreground">{r.ingredients.length} malzeme{r.guide?.prep_minutes > 0 ? ` · ${r.guide.prep_minutes} dk` : ""}</span>
                </span>
                {hasGuide(r) ? <span className="shrink-0 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">{r.guide.steps.length} adım</span>
                  : <span className="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[10px] text-muted-foreground">tarif yok</span>}
              </button>
            )),
          ])}
          {filtered.length === 0 && <p className="p-6 text-sm text-muted-foreground">Ürün bulunamadı</p>}
        </div>

        <div className="rounded-xl border border-border bg-card p-6 lg:col-span-3 print:col-span-5 print:border-0 print:p-0" data-testid="guide-detail" id="guide-print-area">
          {!sel && (
            <div className="flex h-full min-h-64 flex-col items-center justify-center text-center text-muted-foreground">
              <BookOpen className="mb-3 h-10 w-10 opacity-30" /><p className="text-sm">Soldan bir ürün seçin</p>
            </div>
          )}
          {sel && editing && <GuideEditor recipe={sel} onClose={() => setEditing(false)} onSaved={async () => { await load(); setEditing(false); }} />}
          {sel && !editing && (
            <>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex gap-4">
                  <RecipePhoto recipe={sel} canEdit={canEdit(sel)} onChanged={load} className="h-28 w-28 shrink-0 print:h-36 print:w-36" />
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-primary">{sel.category}</p>
                    <h2 className="font-heading text-2xl font-extrabold">{sel.display_name}</h2>
                    {sel.flavors?.length > 0 && <p className="mt-1 text-xs text-muted-foreground">Lezzetler: {sel.flavors.join(" · ")}</p>}
                    {sel.guide?.prep_minutes > 0 && <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-xs font-semibold" data-testid="guide-prep"><Clock className="h-3.5 w-3.5" /> {sel.guide.prep_minutes} dk</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 print:hidden">
                  <Button size="sm" variant="secondary" data-testid="guide-print-btn" onClick={() => window.print()}><Printer className="mr-1.5 h-3.5 w-3.5" /> Yazdır</Button>
                  {canEdit(sel) && <Button size="sm" variant="secondary" data-testid="guide-edit-btn" onClick={() => setEditing(true)}><Pencil className="mr-1.5 h-3.5 w-3.5" /> Hazırlanışı Düzenle</Button>}
                </div>
              </div>

              <div className="mt-6 grid gap-6 md:grid-cols-5">
                <div className="md:col-span-2">
                  <h3 className="flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground"><ChefHat className="h-4 w-4" /> Malzemeler {sel.guide?.custom_ingredients && sel.guide.ingredients?.length > 0 && <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[9px] normal-case tracking-normal" data-testid="guide-custom-badge">özel liste</span>}</h3>
                  <ul className="mt-3 space-y-1.5" data-testid="guide-ingredients">
                    {(sel.guide?.custom_ingredients && sel.guide.ingredients?.length > 0
                      ? sel.guide.ingredients
                      : sel.ingredients.map((it) => ({ name: it.flavor ? `${it.name} (${it.flavor})` : it.name, amount: `${formatNum(it.grams, it.grams % 1 ? 2 : 0)} ${it.unit || "g"}`, measure: it.measure }))
                    ).map((it, i) => (
                      <li key={`${it.name}-${i}`} className="flex items-center justify-between gap-2 rounded-lg bg-secondary/40 px-3 py-1.5 text-sm">
                        <span className="min-w-0"><span className="font-medium">{it.name}</span>{it.measure && <span className="block text-[11px] text-muted-foreground" data-testid={`guide-measure-${i}`}>{it.measure}</span>}</span>
                        <span className="shrink-0 font-mono text-xs text-muted-foreground">{it.amount}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-xs text-muted-foreground">Toplam ≈ {formatNum(sel.total_weight, 0)} g · {formatNum(sel.total_calories, 0)} kcal · Protein {formatNum(sel.total_protein, 1)} g</p>
                </div>
                <div className="md:col-span-3">
                  <h3 className="flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground"><BookOpen className="h-4 w-4" /> Hazırlanış</h3>
                  {hasGuide(sel) ? (
                    <ol className="mt-3 space-y-3" data-testid="guide-steps">
                      {sel.guide.steps.map((s, i) => (
                        <li key={i} className="flex gap-3">
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary font-heading text-sm font-extrabold text-primary-foreground">{i + 1}</span>
                          <p className="pt-1 text-sm leading-relaxed">{s}</p>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <div className="mt-3 rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="guide-empty">
                      Bu ürün için henüz hazırlanış yazılmamış.{canEdit(sel) && <> <button className="font-semibold text-primary underline-offset-2 hover:underline" onClick={() => setEditing(true)}>Şimdi yazın</button></>}
                    </div>
                  )}
                  {sel.guide?.tip && (
                    <div className="mt-4 flex gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm" data-testid="guide-tip">
                      <Lightbulb className="h-5 w-5 shrink-0 text-amber-500" /><p className="whitespace-pre-line">{sel.guide.tip}</p>
                    </div>
                  )}
                  {sel.guide?.updated_by && <p className="mt-4 text-[11px] text-muted-foreground">Son güncelleme: {sel.guide.updated_by} · {new Date(sel.guide.updated_at).toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" })}</p>}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
